import React, {useState, useEffect, useContext } from "react";
import useAxios from "../../utils/useAxios";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import ErrorMessage from "../../components/ErrorMessage";
import SelectInput from "../../components/SelectInput";
import PhoneInput from "react-phone-input-2";
import { toast } from "react-toastify";
import {sports} from "../../constants";
import AuthContext from "../../contexts/AuthContext";
import FormImageUpload from "../../components/CustomModel/FormImageUpload";
import useSubmitLock from "../../hooks/useSubmitLock";

const initialData = {
    name: "",
    sport: 1,
    domain: "",
    email: "",
    phone: "",
    instagram: "",
    facebook: "",
};

const ClubForm = ({ clubId = null, onSuccess, onClose, img, width, height }) => {
    const { user } = useContext(AuthContext);
    const { t } = useTranslation();
    const api = useAxios();
    const [formData, setFormData] = useState(initialData);
    const [errors, setErrors] = useState({});
    const [file, setFile] = useState(img);
    const { isSubmitting, wrapSubmit } = useSubmitLock();

    useEffect(() => {
        if (clubId) {
            api.get(`/api/v1.0/clubs/${clubId}/`)
                .then(res => {
                    setFormData(res.data);
                    if (res.data.image) {
                        setFile(res.data.image);
                    }
                })
                .catch(err => console.error(err));
        } else {
            setFormData(initialData);
            setFile(null);
        }
    }, [clubId]);

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = wrapSubmit(async () => {
        try {
            const data = new FormData();
            Object.entries(formData).forEach(([key, value]) => {
                if (value !== null && value !== undefined && key !== "image") {
                    data.append(key, value);
                }
            });
            if (file instanceof File || (file?.file instanceof File)) {
                data.append("image", file.file || file);
            }
            const config = { headers: { "Content-Type": "multipart/form-data" } };
            const response = clubId
                ? await api.patch(`/api/v1.0/clubs/${clubId}/`, data, config)
                : await api.post(`/api/v1.0/clubs/`, data, config);
            onSuccess?.(response.data);
            toast.success(t(clubId ? "Data updated successfully" : "Club created successfully"));
            onClose?.();
        } catch (error) {
            setErrors(error.response?.data || {});
        }
    });

    const handleDelete = async () => {
        const confirmed = window.confirm(t("Are you sure you want to delete this club?"));
        if (!confirmed) return;

        try {
            await api.delete(`/api/v1.0/clubs/${clubId}/`);
            toast.success(t("Club deleted successfully"));
            onSuccess?.();
            onClose?.();
        } catch (error) {
            toast.error(t("Error deleting club"));
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <h2>
                <FeatherIcon icon="award"/>
                {clubId ? t("Edit club") : t("Creating a club")}
            </h2>

            {/* Name */}
            <div className="form-group">
                <label>{t("name")}</label>
                <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-control"
                />
                <ErrorMessage error={errors.name && t(errors.name)} />
            </div>

            <div className="form-group">
                <SelectInput
                    label_text={t("Sport")}
                    default_text={t("Sport")}
                    input_name="sport"
                    default_value={formData.sport}
                    setInputValue={(val) => setFormData(prev => ({ ...prev, sport: val }))}
                    data={sports}
                />
                <ErrorMessage error={errors.sport && t(errors.sport)} />
            </div>

            {/* Email */}
            <div className="form-group">
                <label>{t("email")}</label>
                <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-control"
                />
                <ErrorMessage error={errors.email && t(errors.email)} />
            </div>

            {/* Phone */}
            <div className="form-group">
                <label>{t("phone")}</label>
                <PhoneInput
                    country="ua"
                    value={formData.phone}
                    onChange={phone => setFormData(prev => ({ ...prev, phone }))}
                    inputClass="form-control"
                    specialLabel={null}
                    placeholder={null}
                />
                <ErrorMessage error={errors.phone && t(errors.phone)} />
            </div>

            {/* Socials */}
            {["instagram", "facebook"].map((field) => (
                <div key={field} className="form-group">
                    <label>{t(field)}</label>
                    <input
                        type="text"
                        name={field}
                        value={formData[field] ?? ""}
                        onChange={handleChange}
                        className="form-control"
                    />
                    <ErrorMessage error={errors[field] && t(errors[field])} />
                </div>
            ))}

            {/* image */}
            <div className="form-group form-image">
                <FormImageUpload
                    path="clubs"
                    id={clubId}
                    file={file}
                    setFile={setFile}
                    width={(typeof file === 'object' && file?.width) || width}
                    height={(typeof file === 'object' && file?.height) || height}
                    name="Icon"
                />
                <ErrorMessage error={errors.image && t(errors.image)} />
            </div>

            <br/>
            <ul className="tabs">
                <li>
                    <a
                        href="#"
                        className={`action-button ${isSubmitting ? "disabled" : ""}`}
                        onClick={(e) => {
                            e.preventDefault();
                            if (isSubmitting) return;
                            e.currentTarget.closest('form').requestSubmit();
                        }}
                    >
                        <FeatherIcon icon="check" /> {t("Save")}
                    </a>
                </li>
                {clubId && formData.creator === user.user_id &&
                    <li>
                        <a href="#" className="action-button" onClick={handleDelete}>
                            <FeatherIcon icon="trash-2" /> {t("Delete")}
                        </a>
                    </li>
                }
            </ul>
            <br/>
        </form>
    );
};

export default ClubForm;
